<template>
    <div class="mainContainer">
        <div class="sliderContainer" ref="container" :onmouseup="moveSlider" :onmouseleave="stopSliding" :onmousemove="slide" :onmousedown="startSliding">
            <div class="backFill"></div>
            <div v-if="active && range" class="thumb" :onmousedown="startSlidingLeft">
                <span class="scoreTooltip" v-if="isSliding">{{ minValue + 1 }}</span>
            </div>
            <div class="fill"></div>
            <div class="thumb" :onmousedown="startSlidingRight">
                <span class="scoreTooltip" v-if="isSliding">{{ maxValue }}</span>
            </div>
        </div>
        <div class="labels">
            <p v-if="!hideLabel" class="label">{{ label }}</p>
            <button v-if="active && !hideDiscardButton" class="discard" @click="discard" title="Discard Metric">
                <cross-icon style="margin-top: -1px;" :height="18" :width="18" iconColor="var(--darkgrey)"/>
            </button>
        </div>
    </div>
</template>
  
<script lang="ts">
import WithColorMixin from '@/mixins/WithColorMixin.vue';
import { FilterValues } from '@/types/score';
import { PropType } from 'vue';
import { defineComponent } from 'vue';


export default defineComponent({
    name: 'ValueSlider',
    emits: ["discardMetric", "update:modelValue"],
    props: {
        label: {
            type: String
        },
        modelValue: {
            type: [Object, Number] as PropType<FilterValues>,
            default: () => {return {minValue: 0, maxValue: 1}}
        },
        width: {
            type: Number,
            default: 100
        },
        height: {
            type: Number,
            default: 15
        },
        active: {
            type: Boolean,
            default: false
        },
        range: {
            type: Boolean,
            default: false
        },
        hideDiscardButton: {
            type: Boolean,
            default: false
        },
        hideLabel: {
            type: Boolean,
            default: false
        }
    },
    mixins: [WithColorMixin],
    data () {
        return {
            minValue: 0,
            maxValue: 1,
            slidingLeft: false,
            slidingRight: false
        }
    },
    mounted () {
        if (typeof this.modelValue === "number") {
            this.minValue = 0
            this.maxValue = this.modelValue
        } else {
            this.minValue = Math.max(this.modelValue.minValue - 1, 0)
            this.maxValue = Math.max(this.modelValue.maxValue, 1)
        }
    },
    computed: {
        stepWidth (): number {
            return (this.width / 5)
        },
        sliderWidth (): string {
            return this.width + "px"
        },
        sliderHeight (): string {
            return this.height + "px"
        },
        backFillWidth (): string {
            return this.stepWidth * (this.minValue) + "px"
        },
        fillWidth (): string {
            return this.stepWidth * (this.maxValue - this.minValue) + "px"
        },
        thumbHeigth (): string {
            return this.height + 2 + "px"
        },
        thumbHoverColor (): string {
            return this.active ? "var(--cream)" : "var(--white)"
        },
        isSliding (): boolean {
            return this.slidingLeft || this.slidingRight
        },
        sliderPosition(): number {
            return (this.$refs["container"] as any).getBoundingClientRect().x
        }
    },
    methods: {
        startSliding (event: any) {
            const position = event.pageX
            if (this.range) {
                // If the cursor stands from the first half of the selected bar or left of the left thumb.
                if (position - (this.maxValue - this.minValue) / 2 * this.stepWidth < this.sliderPosition + this.minValue * this.stepWidth)
                    this.slidingLeft = this.active
                else 
                    this.slidingRight = this.active
            } else {
                this.slidingRight = this.active
            }
        },
        startSlidingRight () {
            this.slidingRight = this.active
        },
        startSlidingLeft () {
            this.slidingLeft = this.active && this.range
        },
        stopSliding (event: any) {
            if (this.active && this.slidingRight) {
                this.updateMaxValueFromPosition(event.pageX)
                this.slidingRight = false
            }
            if (this.active && this.slidingLeft) {
                this.updateMinValueFromPosition(event.pageX)
                this.slidingLeft = false
            }
        },
        slide (event: any) {
            if (this.active && this.slidingRight) {
                this.updateMaxValueFromPosition(event.pageX)
            }
            if (this.active && this.slidingLeft) {
                this.updateMinValueFromPosition(event.pageX)
            }
        },
        moveSlider (event: any) {
            const position = event.pageX

            if (this.active) {
                if (this.slidingLeft) {
                    this.updateMinValueFromPosition(position)
                    this.slidingLeft = false
                } else if (this.slidingRight) {
                    this.updateMaxValueFromPosition(position)
                    this.slidingRight = false
                }
            }
        },
        updateMaxValueFromPosition(position: number) {
            this.maxValue = Math.max(Math.floor((position - this.sliderPosition - this.stepWidth / 2) / this.stepWidth) + 1, this.minValue + 1)
            this.emit()
        },
        updateMinValueFromPosition(position: number) {
            this.minValue = Math.min(Math.floor((position - this.sliderPosition - this.stepWidth / 2) / this.stepWidth) + 1, this.maxValue - 1)
            this.emit()
        },
        emit () {
          this.$emit('update:modelValue', {minValue: this.minValue + 1, maxValue: this.maxValue})
        },
        discard ()  {
            this.$emit("discardMetric", this.label)
        }
    }
});

</script>

<style scoped>

div.mainContainer {
    display: flex;
    flex-direction: column;
}

div.sliderContainer {
    margin: 5px;
    display: flex;
    height: v-bind("sliderHeight");
    width: v-bind("sliderWidth");
    background-color: v-bind("lightColor");
    outline: solid;
    outline-width: 2px;
    outline-color: v-bind("darkColor");
}

div.backFill {
    height: v-bind("sliderHeight");
    width: v-bind("backFillWidth");
    background-color: v-bind("lightColor");
    transition: width 0.3s;
}

div.fill {
    height: v-bind("sliderHeight");
    width: v-bind("fillWidth");
    background-color: v-bind("darkColor");
    transition: width 0.3s;
}

div.thumb {
    height: v-bind("thumbHeigth");
    width: 3px;
    background-color: var(--white);
    z-index: 1;
    margin-top: -1px;
}

div.thumb:hover {
    background-color: v-bind("thumbHoverColor");
}

span.scoreTooltip {
    user-select: none;
    color: var(--darkgrey);
    position: relative;
    bottom: -15px;
    left: -2px;
}
div.labels {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 2px 0 7px 0;
    max-height: 30px;
}
p.label {
    font-size: 1.4pc;
}

button.discard {
    border: none;
    transition: all 0.1s;
    margin-left: auto;
    height: 20px;
    width: 20px;
    background: none;
}

button.discard:hover {  
    background-color: var(--lightred);
}
button.discard:active {
    background-color: var(--cream);
}
</style>
  